import React from 'react';
import {
    Button,
    Menu,
    MenuItem,
    makeStyles,
    createStyles,
    Theme,
    ListItemText,
    Typography,
} from '@material-ui/core';
import { FilterList, ExpandMore, PlayArrow, Refresh } from '@material-ui/icons';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        filterContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(1, 0),
            flexWrap: 'wrap',
        },
        filterButton: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(0.5, 1.5),
            textTransform: 'none',
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.text.primary,
            },
        },
        activeFilter: {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: theme.palette.primary.light,
            },
        },
        processButton: {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.common.white,
            border: 'none',
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(0.5, 2),
            textTransform: 'none',
            '&:hover': {
                backgroundColor: theme.palette.success.dark,
            },
            '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
            },
        },
        resetButton: {
            backgroundColor: theme.palette.grey[500],
            color: theme.palette.common.white,
            border: 'none',
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(0.5, 1.5),
            textTransform: 'none',
            '&:hover': {
                backgroundColor: theme.palette.grey[700],
            },
        },
        filterIcon: {
            marginRight: theme.spacing(0.5),
            fontSize: '1.2rem',
        },
        expandIcon: {
            marginLeft: theme.spacing(0.5),
            fontSize: '1.2rem',
        },
        menuPaper: {
            maxHeight: 300,
            width: '220px',
            marginTop: theme.spacing(1),
        },
        menuHeader: {
            padding: theme.spacing(1, 2),
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontWeight: 600,
            color: theme.palette.text.primary,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        clearButton: {
            cursor: 'pointer',
            fontSize: '0.75rem',
            color: theme.palette.error.main,
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        menuItem: {
            padding: theme.spacing(0.5, 1),
        },
        selectedMenuItem: {
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
                backgroundColor: theme.palette.primary.light,
            },
        },
        noResultsMessage: {
            color: theme.palette.warning.main,
            marginLeft: theme.spacing(2),
            padding: theme.spacing(0.5, 1),
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.warning.main}`,
            fontSize: '0.875rem',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
        },
    })
);

interface FilterButtonsProps {
    data: any[];
    headers?: Record<string, string>;
    onFilterChange: (filters: FilterState) => void;
    filters: FilterState;
    onProcess: (visibleColumns: VisibleColumns) => void;
    isProcessed: boolean;
    onReset: () => void;
}

export interface FilterState {
    market: string[];
    portOfLoading: string[];
    portOfDischarge: string[];
}

export interface VisibleColumns {
    showMarket: boolean;
    showPortOfLoading: boolean;
    visiblePortColumns: string[];
}

const FilterButtons: React.FC<FilterButtonsProps> = ({
    data,
    headers = {},
    onFilterChange,
    filters,
    onProcess,
    isProcessed,
    onReset,
}) => {
    const classes = useStyles();

    const [anchorEl, setAnchorEl] = React.useState<{
        market: null | HTMLElement;
        portOfLoading: null | HTMLElement;
        portOfDischarge: null | HTMLElement;
    }>({
        market: null,
        portOfLoading: null,
        portOfDischarge: null,
    });

    const getFilteredData = (data: any[], filters: FilterState, headers: Record<string, string>) => {
        return data.filter(row => {
            if (filters.market.length > 0) {
                if (!row.market || !filters.market.includes(row.market)) return false;
            }

            if (filters.portOfLoading.length > 0) {
                if (!row.port_of_loading || !filters.portOfLoading.includes(row.port_of_loading)) return false;
            }

            if (filters.portOfDischarge.length > 0) {
                const selectedPortKeys = Object.entries(headers)
                    .filter(([key, portName]) =>
                        key.startsWith('port_') &&
                        filters.portOfDischarge.includes(portName)
                    )
                    .map(([key]) => key);

                const hasDataInSelectedPort = selectedPortKeys.some(key => {
                    const value = row[key];
                    return value && value !== '' && value !== '-';
                });

                if (!hasDataInSelectedPort) return false;
            }

            return true;
        });
    };

    const uniqueMarkets = React.useMemo(() => {
        const filteredData = getFilteredData(data, filters, headers);
        const markets = new Set<string>();
        filteredData.forEach(row => {
            if (row.market && row.market.trim() !== '') {
                markets.add(row.market);
            }
        });
        return Array.from(markets)
    }, [data, filters.portOfLoading, filters.portOfDischarge, headers]);

    const uniquePortsOfLoading = React.useMemo(() => {
        const filteredData = getFilteredData(data, filters, headers);
        const ports = new Set<string>();
        filteredData.forEach(row => {
            if (row.port_of_loading && row.port_of_loading.trim() !== '') {
                ports.add(row.port_of_loading);
            }
        });
        return Array.from(ports)
    }, [data, filters.market, filters.portOfDischarge, headers]);

    const uniquePortsOfDischarge = React.useMemo(() => {
        const filteredData = getFilteredData(data, filters, headers);

        const ports = new Set<string>();
        Object.entries(headers).forEach(([key, portName]) => {
            if (key.startsWith('port_') && portName && portName.trim() !== '') {
                const hasData = filteredData.some(row => {
                    const value = row[key];
                    return value && value !== '' && value !== '-';
                });
                if (hasData) {
                    ports.add(portName);
                }
            }
        });
        return Array.from(ports).sort();
    }, [data, filters.market, filters.portOfLoading, headers]);

    const handleClick = (type: 'market' | 'portOfLoading' | 'portOfDischarge', event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(prev => ({ ...prev, [type]: event.currentTarget }));
    };

    const handleClose = (type: 'market' | 'portOfLoading' | 'portOfDischarge') => {
        setAnchorEl(prev => ({ ...prev, [type]: null }));
    };

    const handleToggle = (type: 'market' | 'portOfLoading' | 'portOfDischarge', value: string) => {
        const currentValues = filters[type];
        const newValues = currentValues.includes(value)
            ? currentValues.filter(v => v !== value)
            : [...currentValues, value];

        onFilterChange({
            ...filters,
            [type]: newValues,
        });
    };

    const handleClear = (type: 'market' | 'portOfLoading' | 'portOfDischarge') => {
        onFilterChange({
            ...filters,
            [type]: [],
        });
    };

    const getButtonClass = (type: 'market' | 'portOfLoading' | 'portOfDischarge') => {
        return `${classes.filterButton} ${filters[type].length > 0 ? classes.activeFilter : ''}`;
    };

    const getButtonLabel = (type: 'market' | 'portOfLoading' | 'portOfDischarge', label: string) => {
        const count = filters[type].length;
        return count > 0 ? `${label} (${count})` : label;
    };

    const getMenuItemClass = (type: 'market' | 'portOfLoading' | 'portOfDischarge', value: string) => {
        return filters[type].includes(value) ? classes.selectedMenuItem : '';
    };

    const handleProcess = () => {
        const hasAnyFilter =
            filters.market.length > 0 ||
            filters.portOfLoading.length > 0 ||
            filters.portOfDischarge.length > 0;

        if (!hasAnyFilter) {
            return;
        }

        const visibleColumns: VisibleColumns = {
            showMarket: true,
            showPortOfLoading: true,
            visiblePortColumns: filters.portOfDischarge.length > 0
                ? filters.portOfDischarge
                : uniquePortsOfDischarge,
        };

        onProcess(visibleColumns);

        setAnchorEl({
            market: null,
            portOfLoading: null,
            portOfDischarge: null,
        });
    };

    const hasAnyFilter =
        filters.market.length > 0 ||
        filters.portOfLoading.length > 0 ||
        filters.portOfDischarge.length > 0;

    return (
        <div className={classes.filterContainer}>
            <Button
                className={getButtonClass('market')}
                onClick={(e) => handleClick('market', e)}
                startIcon={<FilterList className={classes.filterIcon} />}
                endIcon={<ExpandMore className={classes.expandIcon} />}
            >
                {getButtonLabel('market', 'Market')}
            </Button>
            <Menu
                anchorEl={anchorEl.market}
                open={Boolean(anchorEl.market)}
                onClose={() => handleClose('market')}
                PaperProps={{ className: classes.menuPaper }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <div className={classes.menuHeader}>
                    <span>Filter by Market</span>
                    {filters.market.length > 0 && (
                        <span className={classes.clearButton} onClick={() => handleClear('market')}>
                            Clear
                        </span>
                    )}
                </div>
                {uniqueMarkets.length === 0 ? (
                    <MenuItem disabled>No markets available</MenuItem>
                ) : (
                    uniqueMarkets.map(market => (
                        <MenuItem
                            key={market}
                            className={`${classes.menuItem} ${getMenuItemClass('market', market)}`}
                            onClick={() => handleToggle('market', market)}
                        >
                            <ListItemText
                                primary={market}
                                primaryTypographyProps={{
                                    style: {
                                        fontWeight: filters.market.includes(market) ? 'bold' : 'normal'
                                    }
                                }}
                            />
                        </MenuItem>
                    ))
                )}
            </Menu>

            <Button
                className={getButtonClass('portOfLoading')}
                onClick={(e) => handleClick('portOfLoading', e)}
                startIcon={<FilterList className={classes.filterIcon} />}
                endIcon={<ExpandMore className={classes.expandIcon} />}
            >
                {getButtonLabel('portOfLoading', 'Port of Loading')}
            </Button>
            <Menu
                anchorEl={anchorEl.portOfLoading}
                open={Boolean(anchorEl.portOfLoading)}
                onClose={() => handleClose('portOfLoading')}
                PaperProps={{ className: classes.menuPaper }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <div className={classes.menuHeader}>
                    <span>Filter by Port of Loading</span>
                    {filters.portOfLoading.length > 0 && (
                        <span className={classes.clearButton} onClick={() => handleClear('portOfLoading')}>
                            Clear
                        </span>
                    )}
                </div>
                {uniquePortsOfLoading.length === 0 ? (
                    <MenuItem disabled>No ports available</MenuItem>
                ) : (
                    uniquePortsOfLoading.map(port => (
                        <MenuItem
                            key={port}
                            className={`${classes.menuItem} ${getMenuItemClass('portOfLoading', port)}`}
                            onClick={() => handleToggle('portOfLoading', port)}
                        >
                            <ListItemText
                                primary={port}
                                primaryTypographyProps={{
                                    style: {
                                        fontWeight: filters.portOfLoading.includes(port) ? 'bold' : 'normal'
                                    }
                                }}
                            />
                        </MenuItem>
                    ))
                )}
            </Menu>

            <Button
                className={getButtonClass('portOfDischarge')}
                onClick={(e) => handleClick('portOfDischarge', e)}
                startIcon={<FilterList className={classes.filterIcon} />}
                endIcon={<ExpandMore className={classes.expandIcon} />}
            >
                {getButtonLabel('portOfDischarge', 'Port of Discharge')}
            </Button>
            <Menu
                anchorEl={anchorEl.portOfDischarge}
                open={Boolean(anchorEl.portOfDischarge)}
                onClose={() => handleClose('portOfDischarge')}
                PaperProps={{ className: classes.menuPaper }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <div className={classes.menuHeader}>
                    <span>Filter by Port of Discharge</span>
                    {filters.portOfDischarge.length > 0 && (
                        <span className={classes.clearButton} onClick={() => handleClear('portOfDischarge')}>
                            Clear
                        </span>
                    )}
                </div>
                {uniquePortsOfDischarge.length === 0 ? (
                    <MenuItem disabled>No ports available</MenuItem>
                ) : (
                    uniquePortsOfDischarge.map(port => (
                        <MenuItem
                            key={port}
                            className={`${classes.menuItem} ${getMenuItemClass('portOfDischarge', port)}`}
                            onClick={() => handleToggle('portOfDischarge', port)}
                        >
                            <ListItemText
                                primary={port}
                                primaryTypographyProps={{
                                    style: {
                                        fontWeight: filters.portOfDischarge.includes(port) ? 'bold' : 'normal'
                                    }
                                }}
                            />
                        </MenuItem>
                    ))
                )}
            </Menu>

            <Button
                className={classes.processButton}
                onClick={handleProcess}
                disabled={!hasAnyFilter}
                startIcon={<PlayArrow />}
            >
                Process
            </Button>

            {isProcessed && (
                <>
                    <Button
                        className={classes.resetButton}
                        onClick={onReset}
                        startIcon={<Refresh />}
                    >
                        Reset
                    </Button>
                </>
            )}
        </div>
    );
};

export default FilterButtons;